import { Controller } from "@hotwired/stimulus"

// The store's money-format config comes from the server as
// window.__SHOP_MONEY__ (emitted by snippets/scripts.liquid from
// Currencies.config) — the SAME shape and values MoneyFormatter uses
// server-side, so a live-recomputed total here formats byte-identically to
// the server-rendered one. `_formatMoney` below reimplements
// MoneyFormatter#grouped_digits; keep the two in sync.
const DEFAULT_MONEY = { code: "VND", symbol: "₫", decimals: 0, position: "suffix", group: ".", decimal: "," }

function shopMoneyConfig() {
  const m = typeof window !== "undefined" ? window.__SHOP_MONEY__ : null
  return m && m.symbol ? m : DEFAULT_MONEY
}

// Connects to data-controller="checkout"
export default class extends Controller {
  static targets = [
    "provinceSelect", "wardSelect",
    "submitButton", "finishButton",
    "stepShipping", "stepPayment", "stepQrScreen", "stepSuccess",
    "successName", "successPhone",
    "displayShipping", "displayDiscount", "discountRow", "displayTotal",
    "subtotalData",
    "couponForm", "couponApplied", "couponInput", "couponButton", "couponCodeLabel", "couponError"
  ]

  connect() {
    this.subtotal = 0
    this.money = shopMoneyConfig()
    this.discountAmt = 0
    this.feeShipping = 0
    if (this.hasSubtotalDataTarget) {
      this.subtotal = parseInt(this.subtotalDataTarget.getAttribute("data-subtotal")) || 0
      this.discountAmt = parseInt(this.subtotalDataTarget.getAttribute("data-discount")) || 0
      this.feeShipping = parseInt(this.subtotalDataTarget.getAttribute("data-shipping")) || 0
    }

    this._loadProvinces()
    this._bindInputClearErrors()
    this._syncDiscountRowVisibility()
    this._updateCartTotals()
  }

  // ==========================================
  // Coupon
  // ==========================================
  async applyCoupon(event) {
    event.preventDefault()
    if (!this.hasCouponInputTarget) return
    const code = this.couponInputTarget.value.trim()
    if (!code) return

    await this._sendCouponRequest("/cart/apply_coupon.js", { code })
  }

  async removeCoupon(event) {
    event.preventDefault()
    await this._sendCouponRequest("/cart/remove_coupon.js", {})
  }

  async _sendCouponRequest(url, body) {
    if (this.hasCouponErrorTarget) {
      this.couponErrorTarget.textContent = ""
      this.couponErrorTarget.classList.add("hidden")
    }

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(body)
      })
      const data = await res.json()

      if (res.ok) {
        this.discountAmt = data.discount_amount || 0
        if (this.hasCouponCodeLabelTarget) this.couponCodeLabelTarget.innerText = data.coupon_code || ""

        const applied = !!data.coupon_code
        if (this.hasCouponFormTarget) this.couponFormTarget.classList.toggle("hidden", applied)
        if (this.hasCouponAppliedTarget) this.couponAppliedTarget.classList.toggle("hidden", !applied)

        this._syncDiscountRowVisibility()
        this._updateCartTotals()
      } else if (this.hasCouponErrorTarget) {
        this.couponErrorTarget.textContent = data.error || "Không thể áp dụng mã giảm giá"
        this.couponErrorTarget.classList.remove("hidden")
      }
    } catch (err) {
      if (this.hasCouponErrorTarget) {
        this.couponErrorTarget.textContent = "Lỗi kết nối. Vui lòng thử lại."
        this.couponErrorTarget.classList.remove("hidden")
      }
    }
  }

  _syncDiscountRowVisibility() {
    if (this.hasDiscountRowTarget) {
      this.discountRowTarget.classList.toggle("hidden", this.discountAmt <= 0)
    }
    if (this.hasDisplayDiscountTarget) {
      this.displayDiscountTarget.innerText = "-" + this._formatMoney(this.discountAmt)
    }
  }

  // ==========================================
  // Address Cascading Selects
  // ==========================================
  // 2 cấp: Tỉnh/Thành phố → Xã/Phường (cấp Quận/Huyện đã bỏ theo cơ cấu
  // hành chính hiện hành — wards' parent_id points straight at the
  // province now, no district in between).
  async changeProvince() {
    const pid = this.provinceSelectTarget.value
    this.wardSelectTarget.innerHTML = '<option value="" disabled selected>Đang tải...</option>'

    const wards = await this._fetchRegions(2, pid)
    this._populateSelect(this.wardSelectTarget, wards, "Chọn Phường/Xã")
  }

  // ==========================================
  // Form Submission
  // ==========================================
  async submitOrder(event) {
    event.preventDefault()
    this._clearAllErrors()

    const btn = this.submitButtonTarget
    btn.disabled = true
    btn.innerText = "Đang xử lý..."

    try {
      const form = this.element.querySelector("form") || this.element.closest("form") || this.element
      const formData = new FormData(form)
      const res = await fetch(form.action, {
        method: "POST",
        headers: { "Accept": "application/json" },
        body: formData
      })

      const data = await res.json()

      if (res.status === 422 && data.errors) {
        this._showFieldErrors(data.errors)
        btn.disabled = false
        btn.innerText = "Thanh toán"
        return
      }

      if (!res.ok) {
        alert("Đã có lỗi xảy ra. Vui lòng thử lại.")
        btn.disabled = false
        btn.innerText = "Thanh toán"
        return
      }

      // Success
      const orderId = data.order?.id
      // Server decides where to go next: straight to the confirmation page,
      // or through the one-click upsell/downsell interstitial first.
      const nextUrl = data.next_url || `/checkout/success?order_id=${orderId}`
      const paymentRadio = this.element.querySelector('input[name="checkout[payment_method]"]:checked')
      const isQr = paymentRadio && paymentRadio.value === "qr"

      if (this.hasStepShippingTarget) this.stepShippingTarget.classList.add("hidden")
      if (this.hasStepPaymentTarget) this.stepPaymentTarget.classList.add("hidden")
      btn.classList.add("hidden")

      if (isQr) {
        if (this.hasStepQrScreenTarget) this.stepQrScreenTarget.classList.remove("hidden")
        if (this.hasFinishButtonTarget) {
          this.finishButtonTarget.classList.remove("hidden")
          this.finishButtonTarget.setAttribute("data-order-id", orderId)
          this.finishButtonTarget.setAttribute("data-next-url", nextUrl)
        }
      } else {
        window.location.href = nextUrl
      }
    } catch (err) {
      console.error("Checkout error:", err)
      alert("Lỗi kết nối. Vui lòng thử lại.")
      btn.disabled = false
      btn.innerText = "Thanh toán"
    }
  }

  finishOrder() {
    const orderId = this.finishButtonTarget.getAttribute("data-order-id")
    const nextUrl = this.finishButtonTarget.getAttribute("data-next-url") || `/checkout/success?order_id=${orderId}`
    window.location.href = nextUrl
  }

  // ==========================================
  // Private Methods
  // ==========================================
  async _loadProvinces() {
    const data = await this._fetchRegions(0)
    if (this.hasProvinceSelectTarget) {
      this._populateSelect(this.provinceSelectTarget, data, "Chọn Tỉnh/Thành phố")
    }
  }

  async _fetchRegions(level, parentId = "") {
    try {
      let url = `/api/regions?level=${level}`
      if (parentId) url += `&parent_id=${parentId}`
      url += `&t=${Date.now()}`
      const res = await fetch(url)
      const data = await res.json()
      return data.data || []
    } catch (e) {
      console.error("Failed to fetch regions", e)
      return []
    }
  }

  _populateSelect(selectElem, data, defaultText) {
    selectElem.innerHTML = `<option value="" disabled selected>${defaultText}</option>`
    data.forEach(item => {
      selectElem.innerHTML += `<option value="${item.id}">${item.name}</option>`
    })
    selectElem.disabled = data.length === 0
  }

  _clearAllErrors() {
    this.element.querySelectorAll(".field-error").forEach(el => {
      el.textContent = ""
      el.classList.add("hidden")
    })
    this.element.querySelectorAll(".checkout-input.field-invalid").forEach(el => {
      el.classList.remove("field-invalid")
    })
  }

  _showFieldErrors(errors) {
    this._clearAllErrors()
    let firstErrorEl = null

    Object.entries(errors).forEach(([field, message]) => {
      const errorEl = this.element.querySelector(`[data-error="${field}"]`)
      if (errorEl) {
        errorEl.textContent = message
        errorEl.classList.remove("hidden")
        if (!firstErrorEl) firstErrorEl = errorEl
      }
      const inputEl = this.element.querySelector(`[data-field="${field}"]`)
      if (inputEl) {
        inputEl.classList.add("field-invalid")
      }
    })

    if (firstErrorEl) {
      firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  _bindInputClearErrors() {
    this.element.querySelectorAll(".checkout-input").forEach(input => {
      const eventType = input.tagName === "SELECT" ? "change" : "input"
      input.addEventListener(eventType, () => {
        const field = input.getAttribute("data-field")
        if (!field) return
        input.classList.remove("field-invalid")
        const errorEl = this.element.querySelector(`[data-error="${field}"]`)
        if (errorEl) {
          errorEl.textContent = ""
          errorEl.classList.add("hidden")
        }
      })
    })
  }

  // Reimplements MoneyFormatter#grouped_digits (Ruby) — explicit group /
  // decimal separators from the shared config, NOT Intl locale formatting,
  // so the output matches the server exactly for every currency.
  _formatMoney(amount) {
    const { symbol, position, decimals, group, decimal } = this.money
    const n = Number(amount) || 0
    const negative = n < 0
    const [whole, frac] = Math.abs(n).toFixed(decimals).split(".")
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group)
    let digits = frac ? `${grouped}${decimal}${frac}` : grouped
    if (negative) digits = `-${digits}`
    return position === "prefix" ? `${symbol}${digits}` : `${digits} ${symbol}`
  }

  _updateCartTotals() {
    if (this.hasDisplayShippingTarget) {
      this.displayShippingTarget.innerText = this._formatMoney(this.feeShipping)
    }
    const total = this.subtotal + this.feeShipping - this.discountAmt
    if (this.hasDisplayTotalTarget) {
      this.displayTotalTarget.innerText = this._formatMoney(total)
    }
  }
}
